
/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-06-29
 */

import React, { ChangeEvent, useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row, Table } from "react-bootstrap";
import { useAuth } from "../../../contexts/AuthContext";
import { formatDateFull } from "../../../component/solution/utils/AppUtil";
import { ImageOff, ArrowLeft, ArrowUp, ArrowDown, UploadCloud, File, FileText, Save } from 'lucide-react';
import { IQuerySelectData } from "../../../component/solution/Interfaces";


interface IItemBase {
  id: string,
  created: string, 
  updated: string | null,
  dirty: boolean,
  touched: string, 
  name: string,
  description: string,
  item_asset_id: string,
  item_asset_url: string, 
  opts: string[],
}

interface IVariantItem {
  id: string;
  colour: string;
  size: string;
  item_asset_id: string;
  price: number;
  name: string;
  item_base_id: string;
  sku: string;
  in_stock: number;
  item_asset_url: string;
  dirty: boolean;
  touched: string;
  created: string;
  updated: string | null;
}


interface ISelectedItemBase {
   id: string,
   name: string,
   description: string,
   item_asset_id: string,
   item_asset_url: string
   variants?: IVariantItem[];
}


export const Products: React.FC = () => {

    const { fetchData }  = useAuth();
    const queryUrlTable: string = `${process.env.REACT_APP_BASE_URL}/${"query/table/data"}`;
    const queryUrlSelect: string = `${process.env.REACT_APP_BASE_URL}/${"query/select/data?includeCount=true"}`;
    
    //const [baseItem, setBaseItem] = useState<IItemBase[]>([]);
    const [tableRows, setTableRows] = useState<React.ReactElement[]>([]);
    const [viewMode, setViewMode] = useState<'default' | 'edit' | 'seo' | 'channels' | 'history'>('default'); 
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    const [channelView, setChannelView] = useState("channel one"); // default to one

    // State to track expanded colors
    const [expandedColors, setExpandedColors] = useState<{ [key: string]: boolean }>({});

    const defaultQueryState: IQuerySelectData = {
      appCode: "ecom",
      selectCode: "item_base_list",  
      where: "",  
      orderBy: "",
      pageNum: 0,
      pageSize: 100,
    };    

    const [queryState, setQueryState] = useState<IQuerySelectData>(defaultQueryState);
    
    const defaultSearchState = {
      name: "",
      description: "",
      created: "",
      updated: "",
    };

    const [searchState, setSearchState] = useState<{ [key: string]: string }>(defaultSearchState);


    const [queryLineState, setQueryLineState] = useState<IQuerySelectData>({
      appCode: "ecom",
      selectCode: "item_list",  
      where: "", 
      orderBy: "",
      pageNum: 0,
      pageSize: 100,
    });

    const [selectedBaseItem, setSelectedBaseItem] = useState<ISelectedItemBase>({
      id: '',
      name: '',
      description: '',
      item_asset_id: '',
      item_asset_url: ''
    });



     //Pagination Testing 
    const [pageNumber, setPageNumber] = useState<number>(0); 
    const [pageSize, setPageSize] = useState<number>(100);   
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);



    useEffect(() => {
        if(viewMode === "default"){
           fetchBaseItems();
           setSelectedBaseItem({
            id: '',
            name: '',
            description: '',
            item_asset_id: '',
            item_asset_url: ''
          });
        }
    }, [queryState, viewMode])

    // Pagination updates
      useEffect(() => {
          setQueryState(prev => ({
              ...prev,
              pageNum: pageNumber,
              pageSize: pageSize,
          }));
      }, [pageNumber, pageSize]);

    const fetchBaseItems = () => {
        fetchData(queryUrlSelect, {method:"POST", body: JSON.stringify(queryState)}, processData );
    }

    const processData = (result: any) => {

     // console.log(`This is the result`, result);

      const data: IItemBase[] = [];

      for (const key in result.data) {
        const item = result.data[key];
        data.push({ ...item.data, ...item.control, id: item.id});
      }

      //setBaseItem(data);
      setTableRows(buildTableRows(data));

      // Pagination 
      setPageSize(pageSize);
      setTotalRecords(result?.numRecords);
      setTotalPages(Math.ceil(result?.numRecords / pageSize));
    }

    const processLineData = (result: any) => {
      //    console.log(`This is the line data result`, result);

          const data: IVariantItem[] = [];

          for (const key in result.data) {
            const item = result.data[key];
            data.push({ ...item.data, ...item.control, id: item.id });
          }

          //Set variants in selectedBaseItem
          setSelectedBaseItem(prev => ({
            ...prev,
            variants: data
          }));
    }

  const stripHtml = (html: string | null | undefined): string => {
      if (!html) return '';
        return html.replace(/<[^>]+>/g, '');
  };

  const cleanHtml = (html: string): string => {
    if (!html) return '';
      return html.replace(/<\/?p>/g, '').trim(); // remove all <p> and </p> tags
   };


  const handleSelectedItem = ( item: Partial<ISelectedItemBase>) => {

    //missing is will abort fetch
    if (!item?.id) return;

      setSelectedBaseItem(prev => ({
      ...prev,
      ...item
    }));
   
    const updatedQueryState: IQuerySelectData = {
      ...queryLineState,
      where: `item_base_id = '${item.id}'`
    };

    //console.log(`Testing`, updatedQueryState);

    fetchData(queryUrlSelect, { method: "POST", headers: { "Content-Type": "application/json"}, body: JSON.stringify(updatedQueryState)}, processLineData);
    setViewMode("edit");

  }


  const buildTableRows = (data: IItemBase[]): React.ReactElement[] => {
    const rowElements: React.ReactElement[] = [];

    const cellStyle: React.CSSProperties = { border: '1px solid #deebed', whiteSpace: 'nowrap', padding: '5px 8px', fontSize: '0.75rem', verticalAlign: 'middle' };

    data.forEach((record, index) => {
      const { id, name, description, item_asset_id, item_asset_url, created, updated } = record;
      rowElements.push(
        <tr id={`${index}`} key={id}>
            <td style={cellStyle}>
            <div className="table-image-container"  onClick={() => { handleSelectedItem({ id, name, description, item_asset_id, item_asset_url }) }} style={{cursor: 'pointer'}}>
                {item_asset_url ? (
                  <img
                    src={item_asset_url}
                    alt={name}
                    title={name} 
                    style={{ height: '100%', width: 'auto', borderRadius: '4px', objectFit: 'contain' }}
                    onError={(e) => (e.currentTarget.style.display = 'none')} 
                  />
                ) : (
                  <ImageOff size={24} color="#ccc" />
                )}
            </div>
          </td>         
            <td style={cellStyle} title={name}>
              {name}
            </td>
            <td style={{...cellStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis',  verticalAlign: 'middle' }} title={stripHtml(description)} >
            <div style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: '1' }}
              dangerouslySetInnerHTML={{ __html: cleanHtml(description || '') }}
              />
          </td>
          <td style={cellStyle} title={formatDateFull(created)}>
            {formatDateFull(created)}
          </td>
          <td style={cellStyle} title={formatDateFull(updated ?? "")}>
            {formatDateFull(updated ?? "")}
          </td> 
          <td style={cellStyle} >
            <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='View'
               onClick={() => { handleSelectedItem({ id, name, description, item_asset_id, item_asset_url }) }}
             >
                <FileText style={{ width: "20px", height: "20px" }}  /> 
            </button>
          </td> 
        </tr>
      );
    });

    return rowElements;
  };

  const handleSearchOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;
    //console.log(`${id} : ${value}`);
    setSearchState((prev) => ({ ...prev, [id]: value.trim() }));
  };

  function parseDate(value: string): string {
    const trimmed = value.trim();
    let date: Date | null = null;

    // Try to match known patterns
    const matchDdMmYyyy = /^\d{2}-\d{2}-\d{4}/.test(trimmed);
    const matchYyyyMmDd = /^\d{4}-\d{2}-\d{2}/.test(trimmed);

    if (matchDdMmYyyy) {
      const [day, month, year] = trimmed.split(/[- ]/);
      date = new Date(`${year}-${month}-${day}`);
    } else if (matchYyyyMmDd) {
      date = new Date(trimmed.split(' ')[0]);
    }

    return date && !isNaN(date.getTime())
      ? date.toISOString().split('T')[0] // Format as yyyy-mm-dd
      : value;
  }

  const generateWhereClause = (search: { [key: string]: string }) => {
    const clauses = Object.entries(search)
      .filter(([_, value]) => value.trim() !== "")
      .map(([key, value]) => {
        if (key === "created" || key === "updated") {
          const parsedDate = parseDate(value);
          return `${key}::date = '${parsedDate.replace(/'/g, "''")}'`;
        } else {
          return `${key} ILIKE '%${value.replace(/'/g, "''")}%'`;
        }
      });
    return clauses.join(" AND ");
  };

   const searchClicked = () => {
    // Reset pagination state
    setPageNumber(0); // Reset page to first page
    setPageSize(100); // Reset page size to 100

    const whereClause = generateWhereClause(searchState);

    setQueryState(prev => ({
      ...prev,
      where: whereClause,
      pageNum: 0, // Ensure that the query state reflects the reset page number
      pageSize: 100, // Ensure the query state reflects the reset page size
    }));
 };
   
  const clearSearchClicked = () => {
     setSearchState(defaultSearchState);
     setQueryState(defaultQueryState);
  }    
   
  

   // Pagination handlers
    const goToFirstPage = () => setPageNumber(0);
    const goToPreviousPage = () => setPageNumber(prev => prev - 1);
    const goToNextPage = () => setPageNumber(prev => prev + 1);
    const goToLastPage = () => setPageNumber(totalPages - 1);

    // Update page size handler
    const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newSize = parseInt(event.target.value, 10);
        setPageSize(newSize);
    };
                

     const thStyle = {
         backgroundColor: '#293042',
         border: '1px solid #deebed',
         fontWeight: 600,
         color:'white',
         padding: '12px 8px', 
         fontSize: '0.75rem'
    };

    const thControlsStyle = {
        textWrap: 'nowrap',
        backgroundColor: 'white', 
        border: '1px solid #deebed',
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding:'2px 0px',
        margin: '0px'

    };

    const thSearchStyle = {
        textWrap: 'nowrap',
        backgroundColor: 'white', //lightgray //white
        border: '1px solid #deebed',
        padding: '8px 4px'
    };


    const sizePriority = [
      "XXXL", "3XL", "XXL", "2XL", "XL",
      "L", "LG",
      "M", "MD",
      "S", "SM",
      "XS", "XXS",
    ];

     
  const groupedVariants = selectedBaseItem?.variants?.reduce((acc, variant) => {
    if (!acc[variant.colour]) {
      acc[variant.colour] = [];
    }
    acc[variant.colour].push(variant);
    return acc;
  }, {} as { [key: string]: IVariantItem[] });

if (groupedVariants) {
  Object.keys(groupedVariants).forEach((color) => {
    groupedVariants[color].sort((a, b) => {
      const sizeA = parseFloat(a.size);
      const sizeB = parseFloat(b.size);

      const isNumericA = !isNaN(sizeA);
      const isNumericB = !isNaN(sizeB);

      if (isNumericA && isNumericB) {
        return sizeB - sizeA; // Largest number first
      }

      if (!isNumericA && !isNumericB) {
        const priorityA = sizePriority.indexOf(a.size.toUpperCase());
        const priorityB = sizePriority.indexOf(b.size.toUpperCase());

        if (priorityA !== -1 && priorityB !== -1) {
          return priorityA - priorityB; // Order from XXL to XS
        }

        // Unknown label sizes fallback to alphabetical
        return a.size.localeCompare(b.size);
      }

      // One is numeric, one is label — sort numeric first
      return isNumericA ? -1 : 1;
    });
  });
}

  

  const toggleColor = (color: string) => {
    setExpandedColors(prev => ({ ...prev, [color]: !prev[color] }));
  };


   // Fields used for rendering the column-specific search inputs in the table header
    const searchFields = [
        { name: 'name', placeholder: 'Search Name...' },
        { name: 'description', placeholder: 'Search Description...' },
        { name: 'created', placeholder: 'Search Created Date...' },
        { name: 'updated', placeholder: 'Search Updated Date...' }
    ];
      

    // Column headers used to render the table column labels in the main table head
    const columnHeaders = [
        { key: 'image', label: 'Image' },
        { key: 'name', label: 'Name' },
        { key: 'description', label: 'Description' },
        { key: 'created', label: 'Created' },
        { key: 'updated', label: 'Updated' },
        { key: 'actions', label: 'Actions' },
    ];



    function slugifyNameForUrl(name: string): string {
      return name
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')           // replace spaces with -
        .replace(/[^a-z0-9\-]/g, '')    // remove non-URL-safe characters
        .replace(/\-+/g, '-');          // collapse multiple dashes

        //beware of double dashes "--"
    }

    
    

    return(
        <React.Fragment>
            <Container fluid className="p-0">
                 {viewMode === "default" && 
                 <Card>
                  <Card.Header style={{padding: "15px 0px 0px 20px"}}>
                      <Card.Title>{"Products"}</Card.Title>               
                  </Card.Header>
                  <CardBody>
                        <div className="d-flex flex-wrap align-items-center justify-content-between mr-2 ml-2 mb-2">
                            <div className="d-flex flex-row gap-2 mt-3">
                                <h6 className="card-subtitle text-muted">Total Records: {totalRecords}</h6>
                                <h6 className="card-subtitle text-muted">Current Page : [ { pageNumber + 1 } of {totalPages} ]</h6>
                            </div>

                            <div className="d-flex align-items-center gap-3">
                                {/* // Page Size */}
                                <nav aria-label="">
                                    <ul className="pagination m-0">
                                        <li className="page-item">
                                            <select className="page-link" 
                                                value={pageSize}
                                                onChange={handlePageSizeChange}
                                                style={{width: '100px', textAlign: 'center', padding: '10px'}}>
                                                <option defaultChecked value={pageSize}>{pageSize}</option>
                                               {[25, 50, 75, 100, 150, 200, 250, 500, 750, 1000].map(size => (
                                                  <option key={size} value={size}>{size}</option>
                                                ))}
                                            </select>
                                        </li>                            
                                    </ul>
                                </nav>

                                {/* // Pagination Control */}
                                <nav aria-label="">
                                    <ul className="pagination m-0">
                                    <li className={`page-item ${pageNumber === 0 ? 'disabled' : ''}`}>
                                        <button 
                                            className="page-link" 
                                            onClick={goToFirstPage}
                                            disabled={pageNumber === 0}
                                            style={{fontSize: '20px', padding: "0px 8px 4px 8px", color: pageNumber === 0 ? "gray" : "black"}}
                                        >
                                            |&larr;
                                        </button>
                                    </li>
                                    <li className={`page-item ${pageNumber <= 0 ? 'disabled' : ''}`}>
                                        <button 
                                            className="page-link" 
                                            onClick={goToPreviousPage}
                                            disabled={pageNumber <= 0}
                                            style={{fontSize: '20px', padding: "0px 8px 4px 8px", color: pageNumber <= 0 ? "gray" : "black"}}
                                        >
                                            &larr;
                                        </button>
                                    </li>
                                    <li className="page-item">
                                        <span className="page-link" style={{ padding: "8px 14px 8px 14px"}}>
                                            Page {pageNumber + 1} of {totalPages}
                                        </span>
                                    </li>
                                    <li className={`page-item ${pageNumber >= totalPages - 1 ? 'disabled' : ''} `}>
                                        <button 
                                            className="page-link" 
                                            onClick={goToNextPage}
                                            disabled={pageNumber >= totalPages - 1}
                                            style={{fontSize: '20px', padding: "0px 8px 4px 8px", color: pageNumber >= totalPages - 1 ? "gray" : "black"}}
                                        >
                                            &rarr;
                                        </button>
                                    </li>
                                    <li className={`page-item ${pageNumber >= totalPages - 1  ? 'disabled' : ''}`}>
                                        <button 
                                            className="page-link" 
                                            onClick={goToLastPage}
                                            disabled={pageNumber >= totalPages - 1}
                                            style={{fontSize: '20px', padding: "0px 8px 4px 8px", color: pageNumber >= totalPages - 1 ? "gray" : "black"}}
                                        >
                                            &rarr;|
                                        </button>
                                    </li>
                                    </ul>
                                </nav>

                            </div>
                        </div> 

                       <div className="sa-table-container" >
                        <table className="table table-bordered sa-table">
                            <>
                            <thead className="sa-list-search">
                            <tr>
                                    <td className="sa-search-control" style={thSearchStyle}></td>                                    
                                    { searchFields.map(({ name, placeholder }) => (
                                         <td key={name} className="sa-search-control" style={thSearchStyle}>
                                          <input id={name} name={name} value={searchState[name] ?? ''} onChange={handleSearchOnChange} type="text" autoComplete="off"  placeholder={placeholder} className="form-control" />
                                        </td>
                                    ))}
        
                                    <td style={thControlsStyle}  className="sa-search-control">
                                        <button onClick={searchClicked} type="button" style={{border:'1px solid rgb(222, 235, 237)', backgroundColor: 'white', borderRadius: '5px', margin: '1px' }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#495057" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="" style={{width: '34px', height: '34px', margin: "0px", padding:"2px"}}>
                                            <circle cx="11" cy="11" r="8"></circle>
                                            <path d="m21 21-4.3-4.3"></path>
                                        </svg>
                                        </button >   
                                        <button onClick={clearSearchClicked} type="button" style={{ border:'1px solid rgb(222, 235, 237)', backgroundColor: 'white', borderRadius: '5px', margin: '1px' }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#495057" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="" style={{width: '34px', height: '34px', margin: "0px"}}>
                                                <path d="M18 6 6 18"></path>
                                                <path d="m6 6 12 12"></path>
                                            </svg>
                                        </button> 
                                    </td>
                                </tr> 
                            </thead>
                            </>

                            <>
                            <thead className="sa-list-columns">
                                <tr>
                                    { columnHeaders.map(({ key, label }) => (
                                        <th key={key} style={thStyle}>{label}</th>
                                    ))}
                                </tr> 
                            </thead>
                            </>
                            <tbody>
                                {tableRows}
                            </tbody>
                        </table>  
                        </div>
                  </CardBody>
                </Card>
                }


                { viewMode !== "default" && 
                 <Card>
                  <Card.Header style={{ padding: "15px 20px 0px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <button
                        className="btn btn-light"
                        title="Back To Products"
                        style={{ borderRadius: "50%", padding: "6px", display: "flex", alignItems: "center", justifyContent: "center",  boxShadow: "0 1px 4px rgba(0,0,0,0.1)", border: '1px solid #cfd0d1' }}
                        onClick={() => {
                          setViewMode("default");
                          setExpandedColors({});
                          setPreviewImage(null);
                        }}
                      >
                        <ArrowLeft size={18} />
                      </button>
                      <Card.Title style={{ margin: 0 }}>Base Product View</Card.Title>
                    </div>                      

                  </Card.Header>
                  
                   <Card.Body style={{ backgroundColor: "#eff2f6", borderRadius: "0 0 0.5rem 0.5rem", margin:'10px 4px 4px 4px', border:'1px solid #e1e5ec'}}>
                       

                    <div className="d-flex border-bottom mb-4" style={{ gap: '1rem' }}>
                     {[
                        { key: 'edit', label: 'Edit Product' },
                        { key: 'seo', label: 'SEO Settings' },
                        { key: 'channels', label: 'Channels' },
                        { key: 'history', label: 'History' },
                      ].map(({ key, label }) => (
                      <button key={key} className={`border-0 bg-transparent fw-semibold px-3 pb-2 position-relative ${ viewMode === key ? 'text-primary' : 'text-muted'}`}
                        style={{ fontSize: '0.85rem', borderBottom: viewMode === key ? '3px solid #0d6efd' : '3px solid transparent', transition: 'all 0.3s ease'}}
                        onClick={() => setViewMode(key as typeof viewMode)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                    { viewMode === "edit" && selectedBaseItem.id && (
                      <> 
                      <p className="text-muted mb-4" style={{paddingLeft: "4px"}}>You are in product viewing mode...</p>

                      {/* Product Title */}
                      <div className="mb-3" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                        <label htmlFor="productTitle" className="form-label fw-semibold">Product Title</label>
                        <input type="text" className="form-control" value={selectedBaseItem.name} id="productTitle" placeholder="Enter product title" disabled />
                      </div>

                      {/* Description */}
                      <div className="mb-3" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                        <label htmlFor="productDescription" className="form-label fw-semibold">Description</label>
                        <textarea className="form-control" value={selectedBaseItem.description} id="productDescription" rows={6} placeholder="Write a detailed description..."  disabled/>
                      </div>

                      {/* Images Section */}
                     <div className="mb-4" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem", border: "1px solid #e1e5ec" }}>
                      <label className="form-label fw-semibold">Base Product Image</label>
                      
                      <div className="d-flex flex-wrap gap-3">
                        {/* Current Image */}
                        <div
                          style={{
                            width: "100px",
                            height: "100px",
                            border: "1px solid #ccc",
                            borderRadius: "10px",
                            overflow: "hidden",
                            background: "#fff",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transition: "transform 0.3s ease",
                          }}
                        >
                          <img
                            src={selectedBaseItem.item_asset_url}
                            alt="Current Product"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              transition: "transform 0.3s ease",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.1)")}
                            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                          />
                        </div>

                         {/* Preview Image (next to original, dotted border) */}
                          {previewImage && (
                          <div
                            style={{
                              width: "100px",
                              height: "100px",
                              border: "2px dotted #aaa",
                              borderRadius: "10px",
                              overflow: "hidden",
                              background: "#f8fff8",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              position: "relative", // important for positioning button
                            }}
                          >
                            <img
                              src={previewImage}
                              alt="Preview"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />

                            {/* Remove Button */}
                            <button
                              type="button"
                              onClick={() => setPreviewImage(null)}
                              style={{
                                position: "absolute",
                                top: "4px",
                                right: "4px",
                                background: "rgba(255, 255, 255, 0.8)",
                                border: "none",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                cursor: "pointer",
                                fontWeight: "bold",
                                color: "#333",
                                padding: 0,
                                lineHeight: 1,
                              }}
                            >
                              ×
                            </button>
                          </div>
                        )}

                        {/* Upload Button */}
                        <label
                          htmlFor="upload-image"
                          style={{
                            width: "100px",
                            height: "100px",
                            border: "1px dashed #aaa",
                            borderRadius: "10px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            background: "#fdfdfd",
                            cursor: "pointer",
                            transition: "background 0.2s ease",
                          }}
                        >
                          <div className="text-center">
                            <div style={{ fontSize: "1.5rem", lineHeight: "1", color: "#666" }}><UploadCloud size={24} /></div>
                            <small className="text-muted">Select New</small>
                          </div>
                          <input
                            id="upload-image"
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setPreviewImage(reader.result as string);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </label>

                       
                      </div>

                      <small className="text-muted d-block mt-1">You can upload a new image to replace the existing one</small>
                    </div>


                      {/* Variants Section */}
                    <div className="mb-3" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem", border: "1px solid #e1e5ec"}}>
                      <label className="form-label fw-semibold">Variants</label>

                      <div className="table-responsive">
                        <table className="table mb-0" style={{ borderCollapse: "collapse" }}>
                          <tbody>
                            {groupedVariants &&
                              Object.keys(groupedVariants).map((color) => (
                                <React.Fragment key={color}>
                                  {/* Color header with dropdown toggle */}
                                  <tr>
                                    <td colSpan={6} style={{ padding: 0, border: "none" }}>
                                      <div
                                        onClick={() => toggleColor(color)}
                                        style={{
                                          padding: "0.75rem",
                                          cursor: "pointer",
                                          border: "1px solid #ddd",
                                          borderRadius: "6px",
                                          backgroundColor: expandedColors[color] ? "#f1f3f5" : "#fff", 
                                          marginBottom: "0.75rem",
                                          transition: "background-color 0.25s ease",
                                        }}
                                      >
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                          {/* Left side: image and text */}
                                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                            {groupedVariants[color][0]?.item_asset_url && (
                                              <div
                                                style={{
                                                  width: "60px",
                                                  height: "60px",
                                                  borderRadius: "6px",
                                                  overflow: "hidden",
                                                  border: "1px solid #ddd",
                                                  flexShrink: 0,
                                                }}
                                              >
                                                <img
                                                  src={groupedVariants[color][0]?.item_asset_url}
                                                  alt={color}
                                                  style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                    transition: "transform 0.3s ease",
                                                  }}
                                                  onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.2)")}
                                                  onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                                                />
                                              </div>
                                            )}
                                            <div style={{ display: "flex", flexDirection: "column", fontSize: "0.775rem", fontWeight: 500 }}>
                                              <span>Name: {groupedVariants[color][0]?.name || "No name"}</span>
                                              <span>Colour: {color}</span>
                                              <span>Total Variants: {groupedVariants[color]?.length || 0}</span>
                                            </div>
                                          </div>

                                          {/* Right side: arrow */}
                                          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                                            {expandedColors[color] ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>


                                  {/* Variant details for this color, collapsed/expanded */}
                                  {expandedColors[color] && groupedVariants[color] && (
                                    <>
                                      {/* Localized table headers */}
                                      <tr className="text-muted" style={{ fontSize: "0.775rem" }}>
                                        <th style={{ border: "none", width: "60px" }}>Image</th>
                                        <th style={{ border: "none" }}>Name</th>
                                        <th style={{ border: "none" }}>Size</th>
                                        <th style={{ border: "none" }}>Color</th>
                                        <th style={{ border: "none" }}>Price</th>
                                        <th style={{ border: "none" }}>Stock</th>
                                      </tr>

                                      {groupedVariants[color].map((variant, idx) => (
                                        <tr key={idx}>
                                          <td style={{ border: "none", paddingTop: "0.75rem" }}>
                                            <div
                                              style={{
                                                width: "60px",
                                                height: "60px",
                                                borderRadius: "6px",
                                                overflow: "hidden",
                                                border: "1px solid #ddd",
                                              }}
                                            >
                                              <img
                                                src={variant.item_asset_url}
                                                alt="Variant"
                                                style={{ width: "100%", height: "100%", objectFit: "cover" , transition: "transform 0.3s ease" }}
                                                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.2)")}
                                                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                                              />
                                            </div>
                                          </td>
                                          <td style={{ border: "none", paddingTop: "0.75rem" }}>
                                            <input type="text" className="form-control" placeholder="Variant name" value={variant.name} disabled />
                                          </td>
                                          <td style={{ border: "none", paddingTop: "0.75rem" }}>
                                            <input type="text" className="form-control" placeholder="Size" value={variant.size} disabled />
                                          </td>
                                          <td style={{ border: "none", paddingTop: "0.75rem" }}>
                                            <input type="text" className="form-control" placeholder="Color" value={variant.colour} disabled />
                                          </td>
                                          <td style={{ border: "none", paddingTop: "0.75rem", position: "relative" }}>
                                            <span style={{ position: "absolute", top: "51%", left: "12px", transform: "translateY(-50%)", color: "#6c757d", pointerEvents: "none", zIndex: 1, paddingLeft: "0.7rem" }}
                                            >
                                              R
                                            </span>
                                            <input type="text" inputMode="decimal" className="form-control" placeholder="0.00" style={{ paddingLeft: "1.4rem" }} value={variant.price}  disabled />
                                          </td>
                                          <td style={{ border: "none", paddingTop: "0.75rem" }}>
                                            <input type="number" className="form-control" placeholder="Stock" value={variant.in_stock} disabled />
                                          </td>
                                        </tr>
                                      ))}
                                    </>
                                  )}
                                </React.Fragment>
                              ))}
                          </tbody>
                        </table>
                      </div>

                      <small className="text-muted d-block mt-2">Add different options like size, color, etc.</small>
                    </div>
                    </>
                    )}


                    { viewMode === "seo" && selectedBaseItem.id && (
                      <>
                        <p className="text-muted mb-4" style={{paddingLeft: "4px"}}>You are in product seo mode...</p>

                        {/* Product Title */}
                        <div className="mb-3" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                          <label htmlFor="productTitle" className="form-label fw-semibold">Product Title</label>
                          <input type="text" className="form-control" value={selectedBaseItem.name} id="productTitle" placeholder="Enter product title" disabled />
                        </div>

                        {/* Description */}
                        <div className="mb-3" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                          <label htmlFor="productDescription" className="form-label fw-semibold">Description</label>
                          <textarea className="form-control" value={selectedBaseItem.description} id="productDescription" rows={6} placeholder="Write a detailed description..."  disabled/>
                        </div>

                         <div className="d-flex justify-content-between align-items-center mb-4 mt-4 px-2">
                            <p className="text-muted m-0">SEO Section...</p>
                            <button className="btn btn-light btn-sm" style={{border: '1px solid #bdbebf'}}>
                             Save SEO Changes
                            </button>
                        </div>

                         {/* Product Meta Tag Title */}
                        <div className="mb-3" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                          <label htmlFor="productTitle" className="form-label fw-semibold">Meta Tag Title</label>
                          <input type="text" className="form-control"  placeholder="Enter product meta tag title" disabled />
                        </div>

                         {/* Meta Tag Description */}
                        <div className="mb-3" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                          <label htmlFor="productDescription" className="form-label fw-semibold">Meta Tag Description</label>
                          <textarea className="form-control" rows={6} placeholder="Write a detailed meta tag description..."  disabled/>
                        </div>

                         {/* Meta Tag Keywords */}
                        <div className="mb-3" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                          <label htmlFor="productDescription" className="form-label fw-semibold">Meta Tag Keywords</label>
                          <textarea className="form-control"  rows={6} placeholder="Write a detailed meta tag keywords..."  disabled/>
                        </div>

                        {/* Url Handle */}
                        <p className="text-muted" style={{paddingLeft: "8px"}}><b>Suggest Url Slug:</b> {`${slugifyNameForUrl(selectedBaseItem.name)}`}</p>
                        
                        <div className="mb-3" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                          <label htmlFor="productTitle" className="form-label fw-semibold">Url Slug</label>
                          <input type="text" className="form-control"  placeholder="Enter url slug" disabled />
                          <p style={{fontSize: '0.7rem'}} className="text-muted d-block mt-2">A URL slug is the last part of the URL address that serves as a unique identifier of the page.</p>
                        </div>
                      </>
                    )}

                    { viewMode === "channels" && selectedBaseItem.id && (
                      <>
                        <p className="text-muted mb-4" style={{ paddingLeft: "4px" }}>
                          You are in product channel mode...
                        </p>

                        {/* Responsive Flex Layout */}
                        <div className="d-flex flex-column flex-md-row gap-4">
                          {/* Left-side Vertical Tabs */}
                           <div
                              className="d-flex flex-md-column border-bottom border-top border-md-0 border-end pe-md-4 gap-2 gap-md-0"
                              style={{
                                minWidth: "180px", // ensures it stays wide enough
                                flexShrink: 0,      // prevents shrinking when content is large
                              }}
                            >
                              {[
                                { key: "channel one", label: "Google Merchant Center" },
                                { key: "channel two", label: "Channel Two" },
                                { key: "channel three", label: "Channel Three" },
                                { key: "channel four", label: "Channel Four" },
                              ].map(({ key, label }) => (
                                <button
                                  key={key}
                                  className={`border-0 bg-transparent fw-semibold px-3 py-2 text-start position-relative w-100 ${
                                    channelView === key ? "text-primary" : "text-muted"
                                  }`}
                                  style={{
                                    fontSize: "0.85rem",
                                    borderLeft: channelView === key ? "3px solid #0d6efd" : "3px solid transparent",
                                    backgroundColor: channelView === key ? "#f8f9fa" : "transparent",
                                    transition: "all 0.3s ease",
                                  }}
                                  onClick={() => setChannelView(key)}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>

                          {/* Right-side Content */}
                          <div className="flex-grow-1">
                            {channelView === "channel one" && <div>
                              <div className="mb-4" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <p className="mb-0 fw-semibold text-muted">BASIC PRODUCT DATA</p>
                                   <button type="button" className="btn btn-sm btn-dark d-flex align-items-center gap-1" style={{borderRadius: "0.5rem"}}>
                                    <Save size={16} />
                                     Save
                                  </button>
                                </div>
                              <hr />
                              <table className="table table-borderless align-middle w-100" >
                                <tbody>
                                  <tr>
                                    <td className="fw-semibold text-muted" style={{ width: "300px" }}>ID</td>
                                    <td><input type="text" className="form-control" placeholder="Enter ID" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Name</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Name" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Description</td>
                                    <td><textarea className="form-control" placeholder="Enter Description" disabled  rows={3}></textarea></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Link URL</td>
                                    <td><input type="url" className="form-control" placeholder="Enter Link URL" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Image URL</td>
                                    <td><input type="url" className="form-control" placeholder="Enter Image URL" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Additional Image URL</td>
                                    <td><input type="url" className="form-control" placeholder="Enter Additional Image URL" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Mobile Link</td>
                                    <td><input type="url" className="form-control" placeholder="Enter Mobile Link" disabled  /></td>
                                  </tr>
                                </tbody>
                              </table>
                              </div>

                              <div className="mb-4" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <p className="mb-0 fw-semibold text-muted">PRICE & AVAILABILITY</p>
                                   <button type="button" className="btn btn-sm btn-dark d-flex align-items-center gap-1" style={{borderRadius: "0.5rem"}}>
                                    <Save size={16} />
                                     Save
                                  </button>
                                </div>
                               <hr />
                              <table className="table table-borderless align-middle w-100" >
                                <tbody>
                                  <tr>
                                    <td className="fw-semibold text-muted" style={{ width: "300px" }}>Availability</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Availability" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Availability Date</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Availability Date" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Cost Of Goods Sold</td>
                                    <td><input type="url" className="form-control" placeholder="Enter Cost Of Goods Sold" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Expiration Date</td>
                                    <td><input type="url" className="form-control" placeholder="Enter Expiration Date" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Price </td>
                                    <td><input type="url" className="form-control" placeholder="Enter Price" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Sale Price</td>
                                    <td><input type="url" className="form-control" placeholder="Enter Sale Price" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Sale Price Effective Date</td> 
                                    <td><input type="url" className="form-control" placeholder="Enter Sale Price Effective Date" disabled  /></td>
                                  </tr>
                                   <tr>
                                    <td className="fw-semibold text-muted">Unit Pricing Measure</td>
                                    <td><input type="url" className="form-control" placeholder="Enter Unit Pricing Measure" disabled  /></td>
                                  </tr>
                                   <tr>
                                    <td className="fw-semibold text-muted">Installment</td> 
                                    <td><input type="url" className="form-control" placeholder="Enter Installment" disabled  /></td>
                                  </tr>
                                   <tr>
                                    <td className="fw-semibold text-muted">Subscription Cost </td> 
                                    <td><input type="url" className="form-control" placeholder="Enter Subscription Cost" disabled  /></td>
                                  </tr>
                                   <tr>
                                    <td className="fw-semibold text-muted">Loyalty Program </td>
                                    <td><input type="url" className="form-control" placeholder="Enter Loyalty Program" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Minimum Price</td>
                                    <td><input type="url" className="form-control" placeholder="Enter Minimum Price" disabled  /></td>
                                  </tr>
                                </tbody>
                              </table>
                              </div>


                              <div className="mb-4" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <p className="mb-0 fw-semibold text-muted">PRODUCT CATEGORY</p>
                                   <button type="button" className="btn btn-sm btn-dark d-flex align-items-center gap-1" style={{borderRadius: "0.5rem"}}>
                                    <Save size={16} />
                                     Save
                                  </button>
                                </div>
                               <hr />
                              <table className="table table-borderless align-middle w-100" >
                                <tbody>
                                  <tr>
                                    <td className="fw-semibold text-muted" style={{ width: "300px" }}>Google Product Category </td>
                                    <td><input type="text" className="form-control" placeholder="Enter Google Product Category" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Product Type</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Product Type" disabled  /></td>
                                  </tr>
                                </tbody>
                              </table>
                              </div>


                              <div className="mb-4" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <p className="mb-0 fw-semibold text-muted">PRODUCT IDENTIFIERS</p>
                                   <button type="button" className="btn btn-sm btn-dark d-flex align-items-center gap-1" style={{borderRadius: "0.5rem"}}>
                                    <Save size={16} />
                                     Save
                                  </button>
                                </div>
                               <hr />
                              <table className="table table-borderless align-middle w-100" >
                                <tbody>
                                  <tr>
                                    <td className="fw-semibold text-muted" style={{ width: "300px" }}>Brand </td>
                                    <td><input type="text" className="form-control" placeholder="Enter Brand" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Global Trade Item Number</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Global Trade Item Number (GTIN)" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Manufacturer Part Number</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Manufacturer Part Number (MPN)" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Identifier Exists</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Identifier Exists" disabled  /></td>
                                  </tr>
                                </tbody> 
                              </table>
                              </div>


                              <div className="mb-4" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <p className="mb-0 fw-semibold text-muted">DETAILED PRODUCT DESCRIPTION</p>
                                   <button type="button" className="btn btn-sm btn-dark d-flex align-items-center gap-1" style={{borderRadius: "0.5rem"}}>
                                    <Save size={16} />
                                     Save
                                  </button>
                                </div>
                               <hr />
                              <table className="table table-borderless align-middle w-100" >
                                <tbody>
                                  <tr>
                                    <td className="fw-semibold text-muted" style={{ width: "300px" }}>Condition </td>
                                    <td><input type="text" className="form-control" placeholder="Enter Condition" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Adult</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Adult" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Multipack</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Multipack" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Bundle</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Bundle" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Certification</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Certification" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Energy Efficiency Class </td>
                                    <td><input type="text" className="form-control" placeholder="Enter Energy Efficiency Class" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Minimum Energy Efficiency Class </td>
                                    <td><input type="text" className="form-control" placeholder="Enter Minimum Energy Efficiency Class" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Maximum Energy Efficiency Class</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Maximum Energy Efficiency Class" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Age Group</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Age Group" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Color</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Color" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Gender</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Gender" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Material</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Material" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Pattern</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Pattern" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Size</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Size" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Size Type</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Size Type" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Size System</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Size System" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Item Group ID</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Item Group ID" disabled  /></td>
                                  </tr>
                                   <tr>
                                    <td className="fw-semibold text-muted">Product Length</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Product Length" disabled  /></td>
                                  </tr>
                                   <tr>
                                    <td className="fw-semibold text-muted">Product Width</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Product Width" disabled  /></td>
                                  </tr>
                                   <tr>
                                    <td className="fw-semibold text-muted">Product Height</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Product Height" disabled  /></td>
                                  </tr>
                                   <tr>
                                    <td className="fw-semibold text-muted">Product Weight</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Product Weight" disabled  /></td>
                                  </tr>
                                   <tr>
                                    <td className="fw-semibold text-muted">Product Detail</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Product Detail" disabled  /></td>
                                  </tr>
                                   <tr>
                                    <td className="fw-semibold text-muted">Product Highlight</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Product Highlight" disabled  /></td>
                                  </tr>
                                </tbody> 
                               </table>
                              </div>


                              <div className="mb-4" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <p className="mb-0 fw-semibold text-muted">DESTINATIONS</p>
                                   <button type="button" className="btn btn-sm btn-dark d-flex align-items-center gap-1" style={{borderRadius: "0.5rem"}}>
                                    <Save size={16} />
                                     Save
                                  </button>
                                </div>
                               <hr />
                              <table className="table table-borderless align-middle w-100" >
                                <tbody>
                                  <tr>
                                    <td className="fw-semibold text-muted" style={{ width: "300px" }}>Excluded Destination</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Excluded Destination" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Included Destination</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Included Destination" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Excluded Countries For Shopping Ads</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Excluded Countries For Shopping Ads" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Pause</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Pause" disabled /></td>
                                  </tr>
                                </tbody> 
                              </table>
                              </div>


                              <div className="mb-4" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <p className="mb-0 fw-semibold text-muted">SHIPPING</p>
                                   <button type="button" className="btn btn-sm btn-dark d-flex align-items-center gap-1" style={{borderRadius: "0.5rem"}}>
                                    <Save size={16} />
                                     Save
                                  </button>
                                </div>
                               <hr />
                              <table className="table table-borderless align-middle w-100" >
                                <tbody>
                                  <tr>
                                    <td className="fw-semibold text-muted" style={{ width: "300px" }}>Shipping</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Shipping" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Shipping Label</td> 
                                    <td><input type="text" className="form-control" placeholder="Enter Shipping Label" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Shipping Weight</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Shipping Weight" disabled  /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Shipping Length</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Shipping Length" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Shipping Width</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Shipping Width" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Shipping Height</td> 
                                    <td><input type="text" className="form-control" placeholder="Enter Shipping Height" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Ships From Country</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Ships From Country" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Maximum Handling Time </td> 
                                    <td><input type="text" className="form-control" placeholder="Enter Maximum Handling Time " disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Shipping Label </td> 
                                    <td><input type="text" className="form-control" placeholder="Enter Shipping Label " disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Minimum Handling Time</td> 
                                    <td><input type="text" className="form-control" placeholder="Enter Minimum Handling Time" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Free Shipping Threshold</td> 
                                    <td><input type="text" className="form-control" placeholder="Enter Free Shipping Threshold" disabled /></td>
                                  </tr>
                                </tbody> 
                              </table>
                              </div>

                              
                               <div className="mb-4" style={{ backgroundColor: "#f9f9f9", padding: "1rem", borderRadius: "0.5rem",  border:'1px solid #e1e5ec' }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <p className="mb-0 fw-semibold text-muted">TAX</p>
                                   <button type="button" className="btn btn-sm btn-dark d-flex align-items-center gap-1" style={{borderRadius: "0.5rem"}}>
                                    <Save size={16} />
                                     Save
                                  </button>
                                </div>
                               <hr />
                              <table className="table table-borderless align-middle w-100" >
                                <tbody>
                                  <tr>
                                    <td className="fw-semibold text-muted" style={{ width: "300px" }}>Tax</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Tax" disabled /></td>
                                  </tr>
                                  <tr>
                                    <td className="fw-semibold text-muted">Tax Category</td>
                                    <td><input type="text" className="form-control" placeholder="Enter Tax Category" disabled  /></td>
                                  </tr>
                                </tbody> 
                              </table>
                              </div>

                              


                             </div>
                            }
                            {channelView === "channel two" && <p>Channel Two content goes here...</p>}
                            {channelView === "channel three" && <p>Channel Three content goes here...</p>}
                            {channelView === "channel four" && <p>Channel Four content goes here...</p>}
                          </div>
                        </div>
                      </>
                    )}

                    { viewMode === "history" && selectedBaseItem.id && (
                      <>
                         <p className="text-muted mb-4" style={{paddingLeft: "4px"}}>You are in product history mode...</p>
                      </>
                    )}
                  </Card.Body>
     


                </Card>
                }
            </Container>
        </React.Fragment>
    );
}

